import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Flame,
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  Repeat,
  Package,
  Sparkles,
  CheckCircle2,
  Eye,
  EyeOff,
  Calendar,
  Layers,
  Settings
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

import API from '../../config/api';

const AdminPujas = () => {
  const [activeTab, setActiveTab] = useState('pujas'); // 'pujas' | 'deities' | 'temple_types' | 'gotras'
  const [pujas, setPujas] = useState([]);
  const [temples, setTemples] = useState([]);
  const [deities, setDeities] = useState([]);
  const [templeTypes, setTempleTypes] = useState([]);
  const [gotras, setGotras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Taxonomy Modal / Inline States
  const [newDeityName, setNewDeityName] = useState('');
  const [newTempleTypeName, setNewTempleTypeName] = useState('');
  const [newGotraName, setNewGotraName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pujasRes, templesRes, deitiesRes, templeTypesRes, gotrasRes] = await Promise.allSettled([
        axios.get(`${API}/pujas?show_hidden=true`),
        axios.get(`${API}/temples`),
        axios.get(`${API}/deities`),
        axios.get(`${API}/temple-types`),
        axios.get(`${API}/gotras`)
      ]);

      if (pujasRes.status === 'fulfilled' && Array.isArray(pujasRes.value.data)) setPujas(pujasRes.value.data);
      if (templesRes.status === 'fulfilled' && Array.isArray(templesRes.value.data)) setTemples(templesRes.value.data);
      if (deitiesRes.status === 'fulfilled' && Array.isArray(deitiesRes.value.data)) setDeities(deitiesRes.value.data);
      if (templeTypesRes.status === 'fulfilled' && Array.isArray(templeTypesRes.value.data)) setTempleTypes(templeTypesRes.value.data);
      if (gotrasRes.status === 'fulfilled' && Array.isArray(gotrasRes.value.data)) setGotras(gotrasRes.value.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load pujas and taxonomies');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (puja) => {
    const nextState = puja.is_active === false ? true : false;
    try {
      await axios.put(
        `${API}/pujas/${puja.id}`,
        { is_active: nextState },
        { withCredentials: true }
      );
      setPujas(prev => prev.map(p => p.id === puja.id ? { ...p, is_active: nextState } : p));
      toast.success(`Puja marked as ${nextState ? 'Active' : 'Hidden'}`);
    } catch (err) {
      console.error('Toggle active error:', err);
      toast.error('Failed to update puja status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sacred puja ceremony?')) return;
    try {
      await axios.delete(`${API}/pujas/${id}`, { withCredentials: true });
      setPujas(pujas.filter(p => p.id !== id));
      toast.success('Puja ceremony removed successfully');
    } catch (error) {
      console.error('Delete puja error:', error);
      toast.error('Failed to delete puja ceremony');
    }
  };

  // Taxonomy Handlers
  const handleAddDeity = async (e) => {
    e.preventDefault();
    if (!newDeityName.trim()) return;
    try {
      const res = await axios.post(`${API}/deities`, { name: newDeityName.trim() }, { withCredentials: true });
      setDeities(prev => [...prev, res.data]);
      setNewDeityName('');
      toast.success('Deity added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add deity');
    }
  };

  const handleDeleteDeity = async (id) => {
    try {
      await axios.delete(`${API}/deities/${id}`, { withCredentials: true });
      setDeities(prev => prev.filter(d => d.id !== id));
      toast.success('Deity removed');
    } catch (err) {
      toast.error('Failed to delete deity');
    }
  };

  const handleAddTempleType = async (e) => {
    e.preventDefault();
    if (!newTempleTypeName.trim()) return;
    try {
      const res = await axios.post(`${API}/temple-types`, { name: newTempleTypeName.trim() }, { withCredentials: true });
      setTempleTypes(prev => [...prev, res.data]);
      setNewTempleTypeName('');
      toast.success('Temple Type added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add temple type');
    }
  };

  const handleDeleteTempleType = async (id) => {
    try {
      await axios.delete(`${API}/temple-types/${id}`, { withCredentials: true });
      setTempleTypes(prev => prev.filter(t => t.id !== id));
      toast.success('Temple Type removed');
    } catch (err) {
      toast.error('Failed to delete temple type');
    }
  };

  const handleAddGotra = async (e) => {
    e.preventDefault();
    if (!newGotraName.trim()) return;
    try {
      const res = await axios.post(`${API}/gotras`, { name: newGotraName.trim() }, { withCredentials: true });
      setGotras(prev => [...prev, res.data]);
      setNewGotraName('');
      toast.success('Gotra added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add gotra');
    }
  };

  const handleDeleteGotra = async (id) => {
    try {
      await axios.delete(`${API}/gotras/${id}`, { withCredentials: true });
      setGotras(prev => prev.filter(g => g.id !== id));
      toast.success('Gotra removed');
    } catch (err) {
      toast.error('Failed to delete gotra');
    }
  };

  const getTempleName = (templeId) => {
    const temple = temples.find(t => t.id === templeId);
    return temple ? temple.name : 'Vedic Temple';
  };

  const filteredPujas = pujas.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    getTempleName(p.temple_id).toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.deity && p.deity.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-200/80 shadow-xs">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2.5">
            <Flame className="w-6 h-6 text-[#FF7B00]" />
            Sacred Pujas &amp; Rituals Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage authentic temple ceremonies, packages, S3 media galleries, and deity taxonomies.
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center gap-1.5 bg-gray-100 p-1 rounded-xl text-xs font-semibold self-start sm:self-auto">
          <button
            onClick={() => setActiveTab('pujas')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeTab === 'pujas' ? 'bg-[#FF7B00] text-white shadow-xs' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Pujas ({pujas.length})
          </button>
          <button
            onClick={() => setActiveTab('deities')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeTab === 'deities' ? 'bg-[#FF7B00] text-white shadow-xs' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Deities ({deities.length})
          </button>
          <button
            onClick={() => setActiveTab('temple_types')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeTab === 'temple_types' ? 'bg-[#FF7B00] text-white shadow-xs' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Temple Types ({templeTypes.length})
          </button>
          <button
            onClick={() => setActiveTab('gotras')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeTab === 'gotras' ? 'bg-[#FF7B00] text-white shadow-xs' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Gotras ({gotras.length})
          </button>
        </div>
      </div>

      {/* ═══════════════════ TAB 1: PUJAS LIST ═══════════════════ */}
      {activeTab === 'pujas' && (
        <>
          <div className="bg-white rounded-xl p-4 shadow-xs border border-gray-200/80 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search pujas by name, temple, or deity..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 text-xs h-10 bg-gray-50/50 border-gray-200"
              />
            </div>
            <Link to="/admin/pujas/new">
              <Button className="btn-saffron text-xs py-2 px-4 flex items-center gap-1.5">
                <Plus className="w-4 h-4" />
                Add New Puja
              </Button>
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-gray-500">
                <div className="w-8 h-8 border-4 border-[#FF7B00] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                Loading Sacred Pujas...
              </div>
            ) : filteredPujas.length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                <Flame className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="font-medium text-gray-600">No pujas found matching your query</p>
                <Link to="/admin/pujas/new" className="inline-block mt-3">
                  <Button className="btn-saffron text-xs">Create First Puja</Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs text-gray-600">
                  <thead className="bg-[#FAF8F5] text-gray-700 font-bold uppercase tracking-wider border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3.5">Puja Name</th>
                      <th className="px-4 py-3.5">Temple &amp; Deity</th>
                      <th className="px-4 py-3.5">Schedule</th>
                      <th className="px-4 py-3.5">Packages</th>
                      <th className="px-4 py-3.5">Base Price</th>
                      <th className="px-4 py-3.5">Status</th>
                      <th className="px-4 py-3.5 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredPujas.map((puja) => {
                      const isVisible = puja.is_active !== false;
                      return (
                        <tr key={puja.id} className="hover:bg-gray-50/60 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 flex items-center justify-center border border-gray-200">
                                {puja.image_url ? (
                                  <img src={puja.image_url} alt={puja.name} className="w-full h-full object-cover" />
                                ) : (
                                  <Flame className="w-5 h-5 text-gray-400" />
                                )}
                              </div>
                              <div>
                                <p className="font-bold text-gray-900">{puja.name}</p>
                                <p className="text-[11px] text-gray-400 font-mono">/puja/{puja.slug || puja.id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-semibold text-gray-800">{getTempleName(puja.temple_id)}</p>
                            <div className="flex items-center gap-1 mt-0.5">
                              {puja.deity && (
                                <span className="text-[10px] text-[#B85A15] bg-orange-50 px-1.5 py-0.5 rounded font-bold border border-orange-200/50">
                                  {puja.deity}
                                </span>
                              )}
                              {puja.temple_type && (
                                <span className="text-[10px] text-[#6E1B34] bg-pink-50 px-1.5 py-0.5 rounded font-bold border border-pink-200/50">
                                  {puja.temple_type}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {puja.is_weekly ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-orange-800 bg-orange-100 px-2 py-0.5 rounded-full">
                                <Repeat className="w-2.5 h-2.5" /> Every {puja.weekly_day || 'Tuesday'}
                              </span>
                            ) : puja.puja_date ? (
                              <span className="text-gray-600 font-medium">
                                {new Date(puja.puja_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </span>
                            ) : (
                              <span className="text-gray-400">Regular</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-gray-700">{puja.packages?.length || 1} package(s)</span>
                          </td>
                          <td className="px-4 py-3 font-bold text-[#FF7B00]">
                            ₹{puja.price?.toLocaleString()}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleToggleActive(puja)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold border transition-colors ${
                                isVisible
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                  : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                              }`}
                              title="Click to toggle visibility"
                            >
                              {isVisible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                              <span>{isVisible ? 'Active' : 'Hidden'}</span>
                            </button>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Link to={`/admin/pujas/edit/${puja.id}`}>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-gray-600 hover:text-[#FF7B00] hover:bg-orange-50 p-1.5 rounded-lg"
                                  title="Edit Puja"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </Link>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDelete(puja.id)}
                                className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg"
                                title="Delete Puja"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ═══════════════════ TAB 2: DEITIES MANAGER ═══════════════════ */}
      {activeTab === 'deities' && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-heading text-lg font-bold text-gray-900">Holy Deities Master ({deities.length})</h3>
              <p className="text-xs text-gray-500">Manage presiding deities (Shri Ganesh, Mahadev, Ma Durga, etc.) available for puja filters.</p>
            </div>
            <form onSubmit={handleAddDeity} className="flex items-center gap-2">
              <Input
                placeholder="Enter deity name..."
                value={newDeityName}
                onChange={(e) => setNewDeityName(e.target.value)}
                className="text-xs h-9 w-52 bg-gray-50/50"
              />
              <Button type="submit" className="btn-saffron text-xs h-9 px-4">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Deity
              </Button>
            </form>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
            {deities.map((d) => (
              <div
                key={d.id || d.name}
                className="p-2.5 rounded-xl border border-gray-200 bg-gray-50/50 flex items-center justify-between gap-1 text-xs"
              >
                <span className="font-bold text-gray-800 truncate">{d.name}</span>
                <button
                  type="button"
                  onClick={() => handleDeleteDeity(d.id)}
                  className="text-gray-400 hover:text-red-600 p-1"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════ TAB 3: TEMPLE TYPES MANAGER ═══════════════════ */}
      {activeTab === 'temple_types' && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-heading text-lg font-bold text-gray-900">Temple Types &amp; Circuits ({templeTypes.length})</h3>
              <p className="text-xs text-gray-500">Categories like Jyotirlinga, Shaktipeeth, Haridwar, Kashi, Panch Kedar, etc.</p>
            </div>
            <form onSubmit={handleAddTempleType} className="flex items-center gap-2">
              <Input
                placeholder="Enter temple type..."
                value={newTempleTypeName}
                onChange={(e) => setNewTempleTypeName(e.target.value)}
                className="text-xs h-9 w-52 bg-gray-50/50"
              />
              <Button type="submit" className="btn-saffron text-xs h-9 px-4">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Type
              </Button>
            </form>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
            {templeTypes.map((tt) => (
              <div
                key={tt.id || tt.name}
                className="p-2.5 rounded-xl border border-gray-200 bg-gray-50/50 flex items-center justify-between gap-1 text-xs"
              >
                <span className="font-bold text-gray-800 truncate">{tt.name}</span>
                <button
                  type="button"
                  onClick={() => handleDeleteTempleType(tt.id)}
                  className="text-gray-400 hover:text-red-600 p-1"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════ TAB 4: GOTRAS MANAGER ═══════════════════ */}
      {activeTab === 'gotras' && (
        <div className="bg-white rounded-2xl p-6 border border-gray-200/80 shadow-xs space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-heading text-lg font-bold text-gray-900">Vedic Gotras Master ({gotras.length})</h3>
              <p className="text-xs text-gray-500">Vedic gotras (Kashyap, Bharadwaj, Vashishta, etc.) for Devotee Sankalp selection.</p>
            </div>
            <form onSubmit={handleAddGotra} className="flex items-center gap-2">
              <Input
                placeholder="Enter gotra name..."
                value={newGotraName}
                onChange={(e) => setNewGotraName(e.target.value)}
                className="text-xs h-9 w-52 bg-gray-50/50"
              />
              <Button type="submit" className="btn-saffron text-xs h-9 px-4">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Gotra
              </Button>
            </form>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
            {gotras.map((g) => (
              <div
                key={g.id || g.name}
                className="p-2.5 rounded-xl border border-gray-200 bg-gray-50/50 flex items-center justify-between gap-1 text-xs"
              >
                <span className="font-bold text-gray-800 truncate">{g.name}</span>
                <button
                  type="button"
                  onClick={() => handleDeleteGotra(g.id)}
                  className="text-gray-400 hover:text-red-600 p-1"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPujas;
